import { useState, useEffect } from 'react';

export const APP_CONFIG_KEY = 'peoplevue_app_config';
export const APP_CONFIG_CHANGE_EVENT = 'peoplevue_config_change';

const API_BASE = import.meta.env.VITE_API_BASE_URL;

// Set by syncAppConfig so a later saveConfig (the admin's own toggle flip)
// can push straight through without the caller having to thread a token.
let apiToken = null;

export const DEFAULT_CONFIG = {
  defaultView: 'grid',
  defaultDisplayMode: 'list',
  defaultSortBy: 'lastName',
  showDepartments: true,
  showExpertise: true,
  showOrgChart: true,
  showAnniversaries: true,
  showRecentContacts: true,
  showFavorites: true,
  showGrowth: true,
  // "Quick Selections" sidebar shortcuts — independently toggleable, not one
  // all-or-nothing switch, since some scopes (e.g. My Office) are far
  // noisier than others (e.g. My Direct Reports) for a given tenant.
  showScopeDirectReports: true,
  showScopeMyTeam: true,
  showScopeManagersTeam: true,
  showScopeMyDepartment: true,
  showScopeMyOffice: true,
};

export function loadConfig() {
  try {
    const stored = localStorage.getItem(APP_CONFIG_KEY);
    return stored ? { ...DEFAULT_CONFIG, ...JSON.parse(stored) } : { ...DEFAULT_CONFIG };
  } catch {
    return { ...DEFAULT_CONFIG };
  }
}

function writeLocal(config) {
  try {
    localStorage.setItem(APP_CONFIG_KEY, JSON.stringify(config));
    window.dispatchEvent(new Event(APP_CONFIG_CHANGE_EVENT));
  } catch {
    // ignore
  }
}

/**
 * Hydrate the local cache from the server. Call once after sign-in, same as
 * every other tenant-wide setting (department settings, exclusions).
 *
 * This is what makes "off for everyone" actually true: without it, an admin's
 * toggle only ever updated their own browser's localStorage, and every other
 * user's client kept reading its own untouched local default forever.
 * Non-fatal on failure — falls back to whatever's already cached locally.
 */
export async function syncAppConfig(token) {
  if (!API_BASE || !token) return false;
  apiToken = token;
  try {
    const res = await fetch(`${API_BASE}/app-config`, {
      headers: { Authorization: `Bearer ${token}` },
      cache: 'no-store',
    });
    if (!res.ok) throw new Error(`App config API ${res.status}`);
    writeLocal({ ...DEFAULT_CONFIG, ...(await res.json()) });
    return true;
  } catch (err) {
    console.warn('Failed to sync app config (non-fatal):', err.message);
    return false;
  }
}

/**
 * Save the tenant-wide config. Local-first like every other admin setting:
 * the local cache updates (and takes effect) immediately, and the server
 * push is admin-gated server-side and non-fatal so an unreachable endpoint
 * never blocks the toggle from working on the admin's own device. Once the
 * push lands, every other user's next syncAppConfig() picks it up.
 */
export function saveConfig(config) {
  writeLocal(config);

  if (API_BASE && apiToken) {
    fetch(`${API_BASE}/app-config`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiToken}` },
      body: JSON.stringify(config),
    })
      .then((res) => { if (!res.ok) console.warn(`App config push failed: ${res.status}`); })
      .catch((err) => console.warn('App config push failed:', err.message));
  }
}

export function useAppConfig() {
  const [config, setConfig] = useState(loadConfig);

  useEffect(() => {
    const handle = () => setConfig(loadConfig());
    window.addEventListener(APP_CONFIG_CHANGE_EVENT, handle);
    return () => window.removeEventListener(APP_CONFIG_CHANGE_EVENT, handle);
  }, []);

  return config;
}
